/**
 * MFA Setup Component
 * 
 * Allows users to enable/disable MFA with either:
 * - Email OTP (6-digit code sent to email)
 * - TOTP (Google Authenticator, Authy, etc.)
 * 
 * Usage: <MFASetup onComplete={() => {}} />
 */

import React, { useState, useEffect } from 'react';
import { Shield, Mail, Smartphone, Copy, Check, X, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { mfaAPI } from '../../utils/api';
import { toast } from 'sonner';

const MFASetup = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState('choose'); // choose, setup, verify, backup, complete
  const [method, setMethod] = useState(null); // email or totp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Setup data
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // MFA status
  const [mfaStatus, setMfaStatus] = useState(null);
  
  // Load MFA status on open
  useEffect(() => {
    if (isOpen) {
      loadMfaStatus();
    }
  }, [isOpen]);
  
  const loadMfaStatus = async () => {
    try {
      const status = await mfaAPI.getStatus();
      setMfaStatus(status);
      if (status.enabled) {
        setStep('enabled');
      } else {
        setStep('choose');
      }
    } catch (err) {
      console.error('Failed to load MFA status:', err);
    }
  };
  
  const startSetup = async (selectedMethod) => {
    setLoading(true);
    setError(null);
    setMethod(selectedMethod);
    
    try {
      const response = await mfaAPI.startSetup(selectedMethod);
      setSetupData(response);
      setStep('verify');
    } catch (err) {
      setError(err.message || 'Failed to start MFA setup');
      toast.error(err.message || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };
  
  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await mfaAPI.verifySetup(method, verificationCode);
      setBackupCodes(response.backup_codes || []);
      setStep('backup');
      toast.success('MFA enabled successfully!');
    } catch (err) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };
  
  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedCode(true);
    toast.success('Backup codes copied to clipboard');
    setTimeout(() => setCopiedCode(false), 2000);
  };
  
  const downloadBackupCodes = () => {
    const codesText = `MFA Backup Codes\n${'='.repeat(30)}\n\nThese codes can be used if you lose access to your authenticator.\nEach code can only be used once.\n\n${backupCodes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleComplete = () => {
    setStep('choose');
    setSetupData(null);
    setVerificationCode('');
    setBackupCodes([]);
    onComplete?.();
    onClose();
  };
  
  const handleClose = () => {
    if (step === 'backup') {
      // Don't close during backup code step - user needs to save them
      return;
    }
    setStep('choose');
    setSetupData(null);
    setVerificationCode('');
    setError(null);
    onClose();
  };
  
  // Render method selection
  const renderChooseMethod = () => (
    <div className="space-y-4">
      <DialogDescription className="text-gray-600">
        Add an extra layer of security to your account by enabling two-factor authentication.
      </DialogDescription>
      
      <div className="grid gap-3">
        <button
          onClick={() => startSetup('email')}
          disabled={loading}
          className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
          data-testid="mfa-method-email"
        >
          <div className="p-3 bg-blue-100 rounded-full">
            <Mail className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Email OTP</h4>
            <p className="text-sm text-gray-500">Receive a 6-digit code via email</p>
          </div>
        </button>
        
        <button
          onClick={() => startSetup('totp')}
          disabled={loading}
          className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
          data-testid="mfa-method-totp"
        >
          <div className="p-3 bg-green-100 rounded-full">
            <Smartphone className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Authenticator App</h4>
            <p className="text-sm text-gray-500">Use Google Authenticator, Authy, or similar</p>
          </div>
        </button>
      </div>
    </div>
  );
  
  // Render verification step
  const renderVerifyStep = () => (
    <div className="space-y-4">
      {method === 'totp' && setupData?.qr_code && (
        <div className="text-center space-y-3">
          <p className="text-sm text-gray-600">
            Scan this QR code with your authenticator app:
          </p>
          <div className="flex justify-center">
            <img 
              src={`data:image/png;base64,${setupData.qr_code}`} 
              alt="MFA QR Code"
              className="w-48 h-48 border rounded-lg"
            />
          </div>
          <div className="text-xs text-gray-500">
            <p>Or enter this code manually:</p>
            <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
              {setupData.totp_secret}
            </code>
          </div>
        </div>
      )}
      
      {method === 'email' && (
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <Mail className="h-8 w-8 text-blue-600 mx-auto mb-2" />
          <p className="text-sm text-gray-700">{setupData?.message}</p>
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="verification-code">Enter verification code</Label>
        <Input
          id="verification-code"
          type="text"
          maxLength={8}
          placeholder="000000"
          value={verificationCode}
          onChange={(e) => {
            setVerificationCode(e.target.value.replace(/\D/g, ''));
            setError(null);
          }}
          className="text-center text-2xl tracking-widest font-mono"
          data-testid="mfa-verification-code"
        />
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
      
      <DialogFooter className="flex gap-2">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={verifyAndEnable} 
          disabled={loading || verificationCode.length < 6}
          data-testid="mfa-verify-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Verify & Enable
        </Button>
      </DialogFooter>
    </div>
  );
  
  // Render backup codes step
  const renderBackupCodes = () => (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator.
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg font-mono text-sm">
        <div className="grid grid-cols-2 gap-2">
          {backupCodes.map((code, i) => (
            <div key={i} className="py-1 text-gray-700">{code}</div>
          ))}
        </div>
      </div>
      
      <div className="flex gap-2">
        <Button variant="outline" onClick={copyBackupCodes} className="flex-1">
          {copiedCode ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
          {copiedCode ? 'Copied!' : 'Copy Codes'}
        </Button>
        <Button variant="outline" onClick={downloadBackupCodes} className="flex-1">
          Download
        </Button>
      </div>
      
      <DialogFooter>
        <Button onClick={handleComplete} data-testid="mfa-setup-done">
          I&apos;ve saved my codes
        </Button>
      </DialogFooter>
    </div>
  );
  
  // Render already enabled state
  const renderEnabledState = () => (
    <div className="space-y-4">
      <div className="text-center p-4">
        <div className="inline-flex p-3 bg-green-100 rounded-full mb-3">
          <Shield className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="font-medium text-gray-900">MFA is Enabled</h3>
        <p className="text-sm text-gray-500 mt-1">
          Using: {mfaStatus?.method === 'totp' ? 'Authenticator App' : 'Email OTP'}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          {mfaStatus?.backup_codes_count} backup codes remaining
        </p>
      </div>
      
      <DialogFooter className="flex-col gap-2">
        <Button variant="outline" onClick={handleClose} className="w-full">
          Close
        </Button>
      </DialogFooter>
    </div>
  );
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            {step === 'enabled' ? 'Two-Factor Authentication' : 
             step === 'backup' ? 'Save Backup Codes' :
             step === 'verify' ? 'Verify Setup' :
             'Enable Two-Factor Authentication'}
          </DialogTitle>
        </DialogHeader>
        
        {loading && step === 'choose' ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {step === 'choose' && renderChooseMethod()}
            {step === 'verify' && renderVerifyStep()}
            {step === 'backup' && renderBackupCodes()}
            {step === 'enabled' && renderEnabledState()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MFASetup;
